import type { CalendarEvent, CalendarPerson } from '@/components/BigCalendar/types';
import type { Appointment } from '@/types/appointments';

const DEFAULT_DURATION_MINUTES = 30;

function staffToCalendarPerson(staff: Appointment['providers'][number]): CalendarPerson {
  const name = [staff.first_name, staff.last_name].filter(Boolean).join(' ').trim();
  return {
    id: staff.id,
    name: name || 'Unknown provider',
    avatarUrl: staff.avatar_url ?? undefined
  };
}

function personToCalendarPerson(
  person: NonNullable<Appointment['participants']>[number]
): CalendarPerson {
  const name = [person.first_name, person.last_name].filter(Boolean).join(' ').trim();
  return {
    id: person.id,
    name: name || 'Unknown participant',
    avatarUrl: person.profile_image_url ?? undefined
  };
}

export function appointmentToCalendarEvent(appointment: Appointment): CalendarEvent | null {
  if (!appointment.id || !appointment.start_at) return null;

  const start = new Date(appointment.start_at);
  const end = appointment.end_at
    ? new Date(appointment.end_at)
    : new Date(
        start.getTime() + (appointment.duration_in_minutes ?? DEFAULT_DURATION_MINUTES) * 60_000
      );

  return {
    id: appointment.id,
    title: appointment.title || 'Appointment',
    start,
    end,
    isCancelled: appointment.meeting_status === 'cancelled',
    isNoShow: appointment.meeting_status === 'no_show',
    providers: appointment.providers?.map(staffToCalendarPerson),
    participants: appointment.participants?.map(personToCalendarPerson),
    description: appointment.description ?? undefined,
    data: appointment
  };
}

export function appointmentsToCalendarEvents(appointments: Appointment[]): CalendarEvent[] {
  return appointments
    .map(appointmentToCalendarEvent)
    .filter((event): event is CalendarEvent => event !== null);
}
